import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import { APP_SUBSCRIPTION_ACTIONS } from '../../constants';
import { Hidden } from '../../../app/styles';

const APP_SUBSCRIPTION_FEATURE_FORM = 'appSubscriptionFeatureForm';
const REQUIRED_FIELDS = ['appInstallationFeature.tag'];

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  return errors;
};

const AppSubscriptionFeatureForm = ({
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  editMode,
  open,
  communityAppId,
  appsubscriptions,
  availableCommunityApps,
}) => {
  const { t } = useTranslation();
  const { action } = useParams();

  const appSubscription = useMemo(
    () => (appsubscriptions || []).find(c => c.id === communityAppId),
    [communityAppId, appsubscriptions]
  );

  const availableCommunityApp = useMemo(
    () => (availableCommunityApps || []).find(c => c.id === communityAppId),
    [communityAppId, availableCommunityApps]
  );

  const availableFeatureOptions = useMemo(() => {
    if (!availableCommunityApp) {
      return [];
    }

    return (availableCommunityApp.communityAppFeatures || [])
      .filter(
        f =>
          f.requiresSubscription &&
          !(appSubscription?.communityAppFeatures || []).find(
            cf => cf.appInstallationFeature?.tag === f.appInstallationFeature?.tag
          )
      )
      .map(f => ({
        key: f.appInstallationFeature.tag,
        value: f.appInstallationFeature.tag,
        text: f.appInstallationFeature.name,
      }));
  }, [appSubscription, availableCommunityApp]);

  if (!open || !communityAppId) return null;

  return (
    <FormModal
      closeIcon
      header={
        action === APP_SUBSCRIPTION_ACTIONS.NEW_FEATURE
          ? t('newFeature')
          : `${t('editFeature')} - ${data?.appInstallationFeature?.name}`
      }
      dataLoading={dataLoading}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={data}
      t={t}
      validate={validate}
      form={APP_SUBSCRIPTION_FEATURE_FORM}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="save"
      saveButtonContent={
        action === APP_SUBSCRIPTION_ACTIONS.NEW_FEATURE ? t('addFeature') : t('editFeature')
      }
    >
      <Grid columns={1}>
        {!editMode ? (
          <Grid.Column>
            <Field
              fluid
              search
              label={t('feature')}
              name="appInstallationFeature.tag"
              valueComponent={SelectFieldValueComponent}
              options={availableFeatureOptions}
              disabled={submitProcessing || dataLoading}
            />
          </Grid.Column>
        ) : (
          <Hidden>
            <Field
              fluid
              search
              label={t('feature')}
              name="appInstallationFeature.tag"
              valueComponent={SelectFieldValueComponent}
              options={availableFeatureOptions}
              disabled={submitProcessing || dataLoading}
            />
          </Hidden>
        )}
      </Grid>
    </FormModal>
  );
};

AppSubscriptionFeatureForm.propTypes = {
  appsubscriptions: PropTypes.array.isRequired,
  availableCommunityApps: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  returnRoute: PropTypes.string.isRequired,
  data: PropTypes.object,
  editMode: PropTypes.bool,
  open: PropTypes.bool,
  communityAppId: PropTypes.string,
};

AppSubscriptionFeatureForm.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  open: false,
  editMode: false,
  communityAppId: undefined,
  data: {},
};

export default AppSubscriptionFeatureForm;
